import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { SpanStatusCode } from '@opentelemetry/api';
import { Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

import { SpanService } from '@ninety/ui/legacy/core/vendor/opentelemetry';
import { environment } from '@ninety/ui/web/environments';

import { NotificationFilterOptions } from '../models/notification-filter-options';
import { NotificationListResponse } from '../models/notifications-list-response';

@Injectable({
  providedIn: 'root',
})
export class NotificationFeedService {
  private readonly http = inject(HttpClient);
  private readonly spanService = inject(SpanService);

  private readonly apiUrl = `${environment.notificationsApiOrigin}/v1/notifications`;

  constructor() {
    this.spanService.setTracer('notification-feed-service');
  }

  getNotificationsInit(): Observable<NotificationListResponse> {
    const span = this.spanService.startSpan('NotificationFeedService.getNotificationsInit');

    return this.http.get<NotificationListResponse>(`${this.apiUrl}/init`).pipe(
      tap(resp => {
        span?.setAttributes({
          notificationCount: resp.notifications.length,
          hasMore: resp.hasMore,
          'result.success': true,
        });
      }),
      catchError((error: unknown) => {
        span?.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }),
      finalize(() => span?.end())
    );
  }

  getNotifications(
    filterBy: NotificationFilterOptions = NotificationFilterOptions.all,
    page = 1
  ): Observable<NotificationListResponse> {
    const span = this.spanService.startSpan('NotificationFeedService.getNotifications', {
      attributes: { page },
    });

    return this.http.get<NotificationListResponse>(`${this.apiUrl}?page=${page}&filter=${filterBy}`).pipe(
      tap(resp => {
        span?.setAttributes({
          notificationCount: resp.notifications.length,
          hasMore: resp.hasMore,
          'result.success': true,
        });
      }),
      catchError((error: unknown) => {
        span?.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }),
      finalize(() => span?.end())
    );
  }

  markNotificationsAsRead(ids: number[]): Observable<void> {
    const span = this.spanService.startSpan('NotificationFeedService.markAsRead', {
      attributes: { count: ids.length },
    });

    return this.http.patch<void>(`${this.apiUrl}/read`, { ids }).pipe(
      tap(() => span?.setAttributes({ 'result.success': true })),
      catchError((error: unknown) => {
        span?.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }),
      finalize(() => span?.end())
    );
  }

  markNotificationAsOpened(id: number): Observable<void> {
    const span = this.spanService.startSpan('NotificationFeedService.markAsOpened', {
      attributes: { notificationId: id },
    });

    return this.http.patch<void>(`${this.apiUrl}/opened/${id}`, {}).pipe(
      tap(() => span?.setAttributes({ 'result.success': true })),
      catchError((error: unknown) => {
        span?.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }),
      finalize(() => span?.end())
    );
  }

  markNotificationAsUnread(id: number): Observable<void> {
    const span = this.spanService.startSpan('NotificationFeedService.markAsUnread', {
      attributes: { notificationId: id },
    });

    return this.http.patch<void>(`${this.apiUrl}/${id}/unread`, {}).pipe(
      tap(() => span?.setAttributes({ 'result.success': true })),
      catchError((error: unknown) => {
        span?.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }),
      finalize(() => span?.end())
    );
  }

  markAllAsRead(): Observable<void> {
    const span = this.spanService.startSpan('NotificationFeedService.markAllAsRead');

    return this.http.patch<void>(`${this.apiUrl}/read/all`, {}).pipe(
      tap(() => span?.setAttributes({ 'result.success': true })),
      catchError((error: unknown) => {
        span?.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }),
      finalize(() => span?.end())
    );
  }
}
